import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  RootState, setReservation, useGetReservationAndSeatsQuery,
} from '../store';
import { Text } from './Text';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import useIsBackoffice from '../hooks/useIsBackoffice';
import { isTicketHoldReservation } from '../utils/ticketHoldReservation';

interface CountdownTimerProps {
  expirationDate: Date;
  // Only shows the time: no server refresh and no redirect when it runs out.
  // For a second copy of the timer (the seat map sheet) next to the main one.
  displayOnly?: boolean;
  className?: string;
}

const secondsUntil = (date: Date) => Math.max(0, Math.floor((date.getTime() - Date.now()) / 1000));

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const CountdownTimer = ({ expirationDate, displayOnly = false, className = 'text-center' }: CountdownTimerProps) => {
  const expiresAt = expirationDate.getTime();
  const [timeLeft, setTimeLeft] = useState(() => secondsUntil(expirationDate));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isBackoffice = useIsBackoffice();
  const { event } = useGetSelectedEvent({ slug: !isBackoffice });
  const reservation = useSelector((state: RootState) => state.orders.reservation);
  const checkingRef = useRef(false);
  const previousPathRef = useRef(location.pathname);

  // The backend extends the seat reservation when the order is created
  // (attachSeatReservation), so the expiry in the cookie can be stale. We ask
  // the server again when the funnel moves to another step and before
  // sending the buyer to the timeout page. Only seat reservations: a
  // general-admission hold is not in that table, so asking would always 404.
  const canRefresh = !displayOnly && !!reservation?.code && !!event?.id && reservation.event_id === event.id
    && !isTicketHoldReservation(reservation);
  const { data: serverReservation, refetch } = useGetReservationAndSeatsQuery({
    businessId: event?.business_id ?? '',
    eventId: event?.id ?? '',
    reservationCode: reservation?.code ?? '',
  }, { skip: !canRefresh });

  // Adopts the server expiry when it is later than ours. Returns whether the
  // reservation is still alive.
  const adoptServerExpiry = (server: { expires_at: string; cleared: boolean } | null | undefined) => {
    if (!server || server.cleared || !reservation) {
      return false;
    }
    const serverExpiry = new Date(server.expires_at).getTime();
    if (serverExpiry > new Date(reservation.expires_at).getTime()) {
      dispatch(setReservation({ ...reservation, expires_at: server.expires_at }));
    }
    return serverExpiry > Date.now() + 1000;
  };

  // refetch() throws synchronously when the query has not started yet.
  const refreshFromServer = async () => {
    const result = await refetch().unwrap();
    return result?.reservation ?? null;
  };

  useEffect(() => {
    if (canRefresh) {
      adoptServerExpiry(serverReservation?.reservation);
    }
  }, [serverReservation]);

  useEffect(() => {
    if (previousPathRef.current === location.pathname) {
      return;
    }
    previousPathRef.current = location.pathname;
    if (canRefresh) {
      // Best effort: if it fails, the check when the timer hits zero asks again.
      refreshFromServer().catch(() => undefined);
    }
  }, [location.pathname]);

  useEffect(() => {
    const target = new Date(expiresAt);
    setTimeLeft(secondsUntil(target));
    const timer = setInterval(() => {
      const remaining = secondsUntil(target);
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  useEffect(() => {
    if (displayOnly || timeLeft > 0 || checkingRef.current) {
      return;
    }
    const goToTimeout = () => navigate(isBackoffice
      ? `/admin/${event?.business_id}/events/${event?.id}/orders/new/timeout`
      : `/events/${event?.slug}/orders/new/timeout`);
    if (!canRefresh) {
      goToTimeout();
      return;
    }
    checkingRef.current = true;
    refreshFromServer()
      .then((server) => {
        if (!adoptServerExpiry(server)) {
          goToTimeout();
        }
      })
      // A reservation the server can no longer find is an expired one: the
      // buyer goes to the timeout page exactly as before this check existed.
      .catch(() => goToTimeout())
      .finally(() => {
        checkingRef.current = false;
      });
  }, [timeLeft, displayOnly, canRefresh]);

  return (
    <div className={className}>
      <Text size='sm' color='text-text-primary' medium>
        Tiempo restante para comprar: {formatTime(timeLeft)}
      </Text>
    </div>
  );
};

export default CountdownTimer;
